import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { trainingCatalogService, partnerTrainingAssignmentsService } from '../../../services/enablementService';
import { partnerService } from '../../../services/partnerService';
import { useToast } from '../../../contexts/ToastContext';

const CATEGORIES = ['all', 'onboarding', 'technical', 'sales', 'certification', 'general'];
const TIERS = ['all', 'Tactical', 'Silver', 'Gold', 'Strategic'];
const DIFFICULTY = ['beginner', 'intermediate', 'advanced'];

const AddCatalogModal = ({ onClose, onSuccess, userId }) => {
  const [form, setForm] = useState({ title: '', description: '', category: 'general', training_type: 'online', duration: '', difficulty_level: 'intermediate', target_tier: '', is_mandatory: false, content_url: '', prerequisites: '', learning_objectives: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleChange = (e) => {
    const val = e?.target?.type === 'checkbox' ? e?.target?.checked : e?.target?.value;
    setForm(prev => ({ ...prev, [e?.target?.name]: val }));
  };
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.title?.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError(null);
    try { await trainingCatalogService?.create({ ...form, catalog_status: 'active' }); onSuccess(); }
    catch (err) { setError(err?.message || 'Failed to create training.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-indigo-50 flex-shrink-0">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Icon name="BookOpen" size={18} className="text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Add Training to Catalog</h2>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500 transition-colors"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14 overflow-y-auto flex-1">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Title *</label><input name="title" value={form?.title} onChange={handleChange} placeholder="e.g. Advanced Product Integration Workshop" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-400" required /></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Description</label><textarea name="description" value={form?.description} onChange={handleChange} rows={2} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 resize-none" /></div>
          <div className="grid grid-cols-2 gap-14">
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Category</label>
              <select name="category" value={form?.category} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
                {CATEGORIES?.filter(c => c !== 'all')?.map(c => <option key={c} value={c}>{c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Type</label>
              <select name="training_type" value={form?.training_type} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
                {['online', 'instructor-led', 'self-paced', 'workshop', 'webinar']?.map(t => <option key={t} value={t}>{t?.charAt(0)?.toUpperCase() + t?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Duration</label><input name="duration" value={form?.duration} onChange={handleChange} placeholder="e.g. 3 days" className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" /></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Difficulty</label>
              <select name="difficulty_level" value={form?.difficulty_level} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
                {DIFFICULTY?.map(d => <option key={d} value={d}>{d?.charAt(0)?.toUpperCase() + d?.slice(1)}</option>)}
              </select></div>
            <div><label className="block text-xs font-semibold text-slate-600 mb-6">Target Tier</label>
              <select name="target_tier" value={form?.target_tier} onChange={handleChange} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
                <option value="">All Tiers</option>
                {TIERS?.filter(t => t !== 'all')?.map(t => <option key={t} value={t}>{t}</option>)}
              </select></div>
            <div className="flex items-center gap-10 pt-20"><input type="checkbox" name="is_mandatory" checked={form?.is_mandatory} onChange={handleChange} className="w-4 h-4 rounded border-slate-300 text-violet-600" /><label className="text-sm font-medium text-slate-700">Mandatory Training</label></div>
          </div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</> : <><Icon name="Plus" size={16} />Add Training</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const AssignModal = ({ item, partners, onClose, onSuccess }) => {
  const [partnerId, setPartnerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!partnerId) { setError('Select a partner.'); return; }
    setSaving(true); setError(null);
    try {
      await partnerTrainingAssignmentsService?.assign({ partner_id: partnerId, training_catalog_id: item?.id, due_date: dueDate || null, assignment_status: 'assigned' });
      onSuccess();
    } catch (err) { setError(err?.message || 'Failed to assign.'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-16">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-24 py-18 border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div className="flex items-center gap-12">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Icon name="UserPlus" size={18} className="text-white" /></div>
            <div><h2 className="text-base font-bold text-slate-900">Assign to Partner</h2><p className="text-xs text-slate-500 mt-1">{item?.title}</p></div>
          </div>
          <button onClick={onClose} className="p-8 rounded-lg hover:bg-slate-100 text-slate-500"><Icon name="X" size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-24 space-y-14">
          {error && <div className="flex items-center gap-10 p-12 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm"><Icon name="AlertCircle" size={15} />{error}</div>}
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Partner *</label>
            <select value={partnerId} onChange={e => setPartnerId(e?.target?.value)} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 bg-white" required>
              <option value="">— Select Partner —</option>
              {partners?.map(p => <option key={p?.id} value={p?.id}>{p?.brand_name || p?.legal_name}</option>)}
            </select></div>
          <div><label className="block text-xs font-semibold text-slate-600 mb-6">Due Date</label><input type="date" value={dueDate} onChange={e => setDueDate(e?.target?.value)} className="w-full px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20" /></div>
          <div className="flex items-center gap-12 pt-8 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 px-20 py-11 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 px-20 py-11 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all disabled:opacity-60 flex items-center justify-center gap-8">
              {saving ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Assigning...</> : <><Icon name="UserPlus" size={16} />Assign</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const difficultyColor = { beginner: 'bg-emerald-100 text-emerald-700', intermediate: 'bg-amber-100 text-amber-700', advanced: 'bg-red-100 text-red-700' };
const categoryColor = { onboarding: 'bg-blue-100 text-blue-700', technical: 'bg-violet-100 text-violet-700', sales: 'bg-orange-100 text-orange-700', certification: 'bg-teal-100 text-teal-700', general: 'bg-slate-100 text-slate-600' };

const TrainingLibrary = () => {
  const toast = useToast();
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({ search: '', category: 'all', tier: 'all' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignItem, setAssignItem] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cat, pts] = await Promise.all([trainingCatalogService?.getAll(), partnerService?.getAll()]);
      setCatalog(cat || []);
      setPartners(pts || []);
    } catch (err) { toast?.error('Failed to load training catalog.'); }
    finally { setLoading(false); }
  };

  const filtered = catalog?.filter(item => {
    if (filters?.category !== 'all' && item?.category !== filters?.category) return false;
    if (filters?.tier !== 'all' && item?.target_tier && item?.target_tier !== filters?.tier) return false;
    if (filters?.search && !item?.title?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-20">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-24">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-16 mb-20">
          <div className="flex items-center gap-14">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
              <Icon name="Library" size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Training Library</h2>
              <p className="text-sm text-slate-500 mt-1">Master catalog of all training programs — assign to partners</p>
            </div>
          </div>
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-8 px-20 py-11 rounded-xl bg-gradient-to-r from-violet-500 to-indigo-600 text-white text-sm font-semibold shadow-md hover:shadow-lg transition-all">
            <Icon name="Plus" size={16} />Add Training
          </button>
        </div>
        {/* Filters */}
        <div className="flex flex-wrap gap-12">
          <div className="relative flex-1 min-w-[200px]">
            <Icon name="Search" size={16} className="absolute left-14 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={filters?.search} onChange={e => setFilters(p => ({ ...p, search: e?.target?.value }))} placeholder="Search trainings..." className="w-full pl-40 pr-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20" />
          </div>
          <select value={filters?.category} onChange={e => setFilters(p => ({ ...p, category: e?.target?.value }))} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
            {CATEGORIES?.map(c => <option key={c} value={c}>{c === 'all' ? 'All Categories' : c?.charAt(0)?.toUpperCase() + c?.slice(1)}</option>)}
          </select>
          <select value={filters?.tier} onChange={e => setFilters(p => ({ ...p, tier: e?.target?.value }))} className="px-14 py-10 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 bg-white">
            {TIERS?.map(t => <option key={t} value={t}>{t === 'all' ? 'All Tiers' : t}</option>)}
          </select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-16">
        {[
          { label: 'Total Trainings', value: catalog?.length, icon: 'BookOpen', color: 'from-violet-500 to-indigo-600' },
          { label: 'Mandatory', value: catalog?.filter(c => c?.is_mandatory)?.length, icon: 'AlertCircle', color: 'from-red-500 to-rose-600' },
          { label: 'Technical', value: catalog?.filter(c => c?.category === 'technical')?.length, icon: 'Wrench', color: 'from-blue-500 to-cyan-600' },
          { label: 'Certifications', value: catalog?.filter(c => c?.category === 'certification')?.length, icon: 'Award', color: 'from-amber-500 to-orange-600' },
        ]?.map((stat, i) => (
          <div key={i} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-20">
            <div className="flex items-center justify-between mb-12">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${stat?.color} flex items-center justify-center`}><Icon name={stat?.icon} size={18} className="text-white" /></div>
              <span className="text-2xl font-bold text-slate-900">{stat?.value}</span>
            </div>
            <p className="text-xs text-slate-500 font-medium">{stat?.label}</p>
          </div>
        ))}
      </div>

      {/* Catalog Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-48"><div className="w-10 h-10 border-4 border-violet-500/20 border-t-violet-500 rounded-full animate-spin" /></div>
      ) : filtered?.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-48 text-center">
          <Icon name="BookOpen" size={40} className="text-slate-300 mx-auto mb-16" />
          <p className="text-slate-500 font-medium">No trainings found</p>
          <p className="text-slate-400 text-sm mt-4">Add your first training to the catalog</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-20">
          {filtered?.map(item => (
            <div key={item?.id} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md hover:border-violet-200 transition-all p-24 flex flex-col gap-16">
              <div className="flex items-start justify-between gap-12">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-8 mb-8">
                    <span className={`px-8 py-3 rounded-lg text-xs font-semibold ${categoryColor?.[item?.category] || 'bg-slate-100 text-slate-600'}`}>{item?.category}</span>
                    <span className={`px-8 py-3 rounded-lg text-xs font-semibold ${difficultyColor?.[item?.difficulty_level] || 'bg-slate-100 text-slate-600'}`}>{item?.difficulty_level}</span>
                    {item?.is_mandatory && <span className="px-8 py-3 rounded-lg text-xs font-semibold bg-red-100 text-red-700">Mandatory</span>}
                  </div>
                  <h3 className="text-sm font-bold text-slate-900 leading-snug">{item?.title}</h3>
                </div>
              </div>
              {item?.description && <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{item?.description}</p>}
              <div className="flex flex-wrap gap-12 text-xs text-slate-500">
                {item?.duration && <span className="flex items-center gap-6"><Icon name="Clock" size={13} />{item?.duration}</span>}
                {item?.training_type && <span className="flex items-center gap-6"><Icon name="Monitor" size={13} />{item?.training_type}</span>}
                {item?.target_tier && <span className="flex items-center gap-6"><Icon name="Star" size={13} />{item?.target_tier}</span>}
              </div>
              <div className="flex items-center gap-10 pt-12 border-t border-slate-100 mt-auto">
                <button onClick={() => setAssignItem(item)} className="flex-1 flex items-center justify-center gap-8 px-16 py-9 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold hover:shadow-md transition-all">
                  <Icon name="UserPlus" size={14} />Assign to Partner
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && <AddCatalogModal onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); loadData(); toast?.success('Training added to catalog.'); }} userId={null} />}
      {assignItem && <AssignModal item={assignItem} partners={partners} onClose={() => setAssignItem(null)} onSuccess={() => { setAssignItem(null); toast?.success('Training assigned to partner.'); }} />}
    </div>
  );
};

export default TrainingLibrary;
